import { Vector3, MeshBuilder, StandardMaterial, Color3, Mesh, DynamicTexture } from '@babylonjs/core';
import { loadModel, playAnimation } from './ModelLoader';
const MONSTER_CONFIGS = {
    wolf: {
        color: new Color3(0.45, 0.42, 0.4),
        accentColor: new Color3(0.6, 0.55, 0.5),
        name: '灰狼',
        bodyScale: 0.7,
    },
    bandit: {
        color: new Color3(0.5, 0.28, 0.15),
        accentColor: new Color3(0.7, 0.4, 0.2),
        name: '山贼',
        bodyScale: 0.9,
    },
    boss_tiger: {
        color: new Color3(1.0, 0.5, 0.05),
        accentColor: new Color3(1.0, 0.8, 0.3),
        name: '猛虎王',
        bodyScale: 1.6,
    },
};
export class MonsterEntity {
    mesh;
    type;
    hp;
    maxHp;
    targetPos;
    hpFill;
    nameplate;
    glbModel = null;
    currentAnim = 'idle';
    lastPos;
    constructor(scene, data) {
        this.type = data.type;
        this.hp = data.hp;
        this.maxHp = data.maxHp;
        this.targetPos = new Vector3(data.x, 0, data.z);
        const cfg = MONSTER_CONFIGS[this.type] || {
            color: new Color3(0.5, 0, 0), accentColor: new Color3(0.7, 0.2, 0.2),
            name: '怪物', bodyScale: 0.8,
        };
        this.mesh = new Mesh('monster', scene);
        // 构建怪物模型
        this.buildModel(scene, cfg);
        // 名字牌
        this.nameplate = this.createNameplate(scene, cfg.name, this.type === 'boss_tiger');
        this.nameplate.parent = this.mesh;
        this.nameplate.position.y = cfg.bodyScale * 2 + 0.6;
        this.nameplate.billboardMode = Mesh.BILLBOARDMODE_ALL;
        // 血条 (背景)
        const hpBarWidth = this.type === 'boss_tiger' ? 1.8 : 1.0;
        const hpBar = MeshBuilder.CreatePlane('mhpBg', { width: hpBarWidth, height: 0.08 }, scene);
        hpBar.position.y = cfg.bodyScale * 2 + 0.35;
        hpBar.parent = this.mesh;
        hpBar.billboardMode = Mesh.BILLBOARDMODE_ALL;
        const bgMat = new StandardMaterial('mhpBgMat', scene);
        bgMat.diffuseColor = new Color3(0.15, 0.15, 0.15);
        bgMat.emissiveColor = new Color3(0.08, 0.08, 0.08);
        hpBar.material = bgMat;
        this.hpFill = MeshBuilder.CreatePlane('mhpFill', { width: hpBarWidth - 0.02, height: 0.06 }, scene);
        this.hpFill.position.y = cfg.bodyScale * 2 + 0.35;
        this.hpFill.position.z = -0.01;
        this.hpFill.parent = this.mesh;
        this.hpFill.billboardMode = Mesh.BILLBOARDMODE_ALL;
        const fillMat = new StandardMaterial('mhpFillMat', scene);
        fillMat.diffuseColor = new Color3(0.9, 0.2, 0.1);
        fillMat.emissiveColor = new Color3(0.5, 0.1, 0.05);
        this.hpFill.material = fillMat;
        this.mesh.position = this.targetPos.clone();
        this.lastPos = this.mesh.position.clone();
        // 异步加载 glb
        this.tryLoadGLB(scene);
    }
    buildModel(scene, cfg) {
        if (this.type === 'wolf') {
            this.buildWolf(scene, cfg);
        }
        else if (this.type === 'boss_tiger') {
            this.buildTiger(scene, cfg);
        }
        else {
            this.buildBandit(scene, cfg);
        }
    }
    buildWolf(scene, cfg) {
        const bodyMat = new StandardMaterial('wolfMat', scene);
        bodyMat.diffuseColor = cfg.color;
        // 身体（椭圆形）
        const body = MeshBuilder.CreateBox('wBody', { width: 0.5, height: 0.4, depth: 0.9 }, scene);
        body.position.y = 0.5;
        body.parent = this.mesh;
        body.material = bodyMat;
        // 头
        const head = MeshBuilder.CreateBox('wHead', { width: 0.35, height: 0.3, depth: 0.4 }, scene);
        head.position = new Vector3(0, 0.55, 0.55);
        head.parent = this.mesh;
        head.material = bodyMat;
        // 鼻子
        const nose = MeshBuilder.CreateBox('wNose', { width: 0.15, height: 0.12, depth: 0.2 }, scene);
        nose.position = new Vector3(0, 0.48, 0.8);
        nose.parent = this.mesh;
        const noseMat = new StandardMaterial('noseMat', scene);
        noseMat.diffuseColor = new Color3(0.2, 0.15, 0.12);
        nose.material = noseMat;
        // 耳朵
        const earMat = new StandardMaterial('earMat', scene);
        earMat.diffuseColor = cfg.accentColor;
        const leftEar = MeshBuilder.CreateBox('wLEar', { width: 0.08, height: 0.15, depth: 0.08 }, scene);
        leftEar.position = new Vector3(-0.12, 0.78, 0.5);
        leftEar.rotation.z = 0.2;
        leftEar.parent = this.mesh;
        leftEar.material = earMat;
        const rightEar = leftEar.clone('wREar');
        rightEar.position.x = 0.12;
        rightEar.rotation.z = -0.2;
        rightEar.parent = this.mesh;
        // 眼睛
        const eyeMat = new StandardMaterial('eyeMat', scene);
        eyeMat.diffuseColor = new Color3(0.9, 0.2, 0.1);
        eyeMat.emissiveColor = new Color3(0.5, 0.1, 0.05);
        const leftEye = MeshBuilder.CreateSphere('wLEye', { diameter: 0.08, segments: 6 }, scene);
        leftEye.position = new Vector3(-0.1, 0.62, 0.7);
        leftEye.parent = this.mesh;
        leftEye.material = eyeMat;
        const rightEye = leftEye.clone('wREye');
        rightEye.position.x = 0.1;
        rightEye.parent = this.mesh;
        // 四条腿
        const legMat = new StandardMaterial('wLegMat', scene);
        legMat.diffuseColor = cfg.color.scale(0.8);
        const positions = [
            new Vector3(-0.18, 0.18, 0.28),
            new Vector3(0.18, 0.18, 0.28),
            new Vector3(-0.18, 0.18, -0.28),
            new Vector3(0.18, 0.18, -0.28),
        ];
        positions.forEach((pos, i) => {
            const leg = MeshBuilder.CreateCylinder(`wLeg${i}`, { height: 0.35, diameter: 0.1, tessellation: 6 }, scene);
            leg.position = pos;
            leg.parent = this.mesh;
            leg.material = legMat;
        });
        // 尾巴
        const tail = MeshBuilder.CreateCylinder('wTail', { height: 0.4, diameterTop: 0.02, diameterBottom: 0.08, tessellation: 6 }, scene);
        tail.position = new Vector3(0, 0.55, -0.55);
        tail.rotation.x = -0.6;
        tail.parent = this.mesh;
        tail.material = bodyMat;
    }
    buildTiger(scene, cfg) {
        const bodyMat = new StandardMaterial('tigerMat', scene);
        bodyMat.diffuseColor = cfg.color;
        // 身体
        const body = MeshBuilder.CreateBox('tBody', { width: 1.0, height: 0.8, depth: 1.6 }, scene);
        body.position.y = 0.8;
        body.parent = this.mesh;
        body.material = bodyMat;
        // 条纹
        const stripeMat = new StandardMaterial('stripeMat', scene);
        stripeMat.diffuseColor = new Color3(0.2, 0.1, 0.0);
        for (let i = 0; i < 4; i++) {
            const stripe = MeshBuilder.CreateBox(`tStripe${i}`, { width: 1.02, height: 0.06, depth: 0.15 }, scene);
            stripe.position = new Vector3(0, 0.8, -0.5 + i * 0.35);
            stripe.parent = this.mesh;
            stripe.material = stripeMat;
        }
        // 头
        const head = MeshBuilder.CreateBox('tHead', { width: 0.7, height: 0.6, depth: 0.6 }, scene);
        head.position = new Vector3(0, 0.95, 1.0);
        head.parent = this.mesh;
        head.material = bodyMat;
        // 嘴巴
        const mouth = MeshBuilder.CreateBox('tMouth', { width: 0.35, height: 0.2, depth: 0.3 }, scene);
        mouth.position = new Vector3(0, 0.78, 1.3);
        mouth.parent = this.mesh;
        const mouthMat = new StandardMaterial('mouthMat', scene);
        mouthMat.diffuseColor = new Color3(1.0, 0.85, 0.6);
        mouth.material = mouthMat;
        // 眼睛 (Boss 发光)
        const eyeMat = new StandardMaterial('tEyeMat', scene);
        eyeMat.diffuseColor = new Color3(1.0, 0.9, 0.2);
        eyeMat.emissiveColor = new Color3(0.8, 0.6, 0.0);
        const leftEye = MeshBuilder.CreateSphere('tLEye', { diameter: 0.12, segments: 8 }, scene);
        leftEye.position = new Vector3(-0.2, 1.05, 1.25);
        leftEye.parent = this.mesh;
        leftEye.material = eyeMat;
        const rightEye = leftEye.clone('tREye');
        rightEye.position.x = 0.2;
        rightEye.parent = this.mesh;
        // 耳朵
        const earMat = new StandardMaterial('tEarMat', scene);
        earMat.diffuseColor = cfg.accentColor;
        const leftEar = MeshBuilder.CreateBox('tLEar', { width: 0.12, height: 0.2, depth: 0.1 }, scene);
        leftEar.position = new Vector3(-0.25, 1.35, 0.95);
        leftEar.parent = this.mesh;
        leftEar.material = earMat;
        const rightEar = leftEar.clone('tREar');
        rightEar.position.x = 0.25;
        rightEar.parent = this.mesh;
        // 四条粗腿
        const legMat = new StandardMaterial('tLegMat', scene);
        legMat.diffuseColor = cfg.color.scale(0.85);
        const positions = [
            new Vector3(-0.35, 0.25, 0.5),
            new Vector3(0.35, 0.25, 0.5),
            new Vector3(-0.35, 0.25, -0.5),
            new Vector3(0.35, 0.25, -0.5),
        ];
        positions.forEach((pos, i) => {
            const leg = MeshBuilder.CreateCylinder(`tLeg${i}`, { height: 0.5, diameter: 0.22, tessellation: 8 }, scene);
            leg.position = pos;
            leg.parent = this.mesh;
            leg.material = legMat;
        });
        // 尾巴
        const tail = MeshBuilder.CreateCylinder('tTail', { height: 0.8, diameterTop: 0.04, diameterBottom: 0.12, tessellation: 6 }, scene);
        tail.position = new Vector3(0, 0.85, -1.0);
        tail.rotation.x = -0.4;
        tail.parent = this.mesh;
        tail.material = bodyMat;
    }
    buildBandit(scene, cfg) {
        const bodyMat = new StandardMaterial('banditBodyMat', scene);
        bodyMat.diffuseColor = cfg.color;
        // 腿
        const legMat = new StandardMaterial('bLegMat', scene);
        legMat.diffuseColor = new Color3(0.3, 0.25, 0.2);
        const ll = MeshBuilder.CreateCylinder('blleg', { height: 0.6, diameterTop: 0.13, diameterBottom: 0.15, tessellation: 6 }, scene);
        ll.position = new Vector3(-0.1, 0.3, 0);
        ll.parent = this.mesh;
        ll.material = legMat;
        const rl = ll.clone('brleg');
        rl.position.x = 0.1;
        rl.parent = this.mesh;
        // 靴子
        const bootMat = new StandardMaterial('bBootMat', scene);
        bootMat.diffuseColor = new Color3(0.2, 0.12, 0.08);
        const lb = MeshBuilder.CreateBox('blboot', { width: 0.15, height: 0.06, depth: 0.2 }, scene);
        lb.position = new Vector3(-0.1, 0.03, 0.02);
        lb.parent = this.mesh;
        lb.material = bootMat;
        const rb = lb.clone('brboot');
        rb.position.x = 0.1;
        rb.parent = this.mesh;
        // 身体（粗壮）
        const body = MeshBuilder.CreateCylinder('bBody', {
            height: 0.8, diameterTop: 0.45, diameterBottom: 0.4, tessellation: 8
        }, scene);
        body.position.y = 0.95;
        body.parent = this.mesh;
        body.material = bodyMat;
        // 腰带（红色）
        const beltMat = new StandardMaterial('bBeltMat', scene);
        beltMat.diffuseColor = new Color3(0.7, 0.15, 0.1);
        const belt = MeshBuilder.CreateTorus('bBelt', { diameter: 0.42, thickness: 0.05, tessellation: 12 }, scene);
        belt.position.y = 0.6;
        belt.parent = this.mesh;
        belt.material = beltMat;
        // 肩甲
        const shoulderMat = new StandardMaterial('bShMat', scene);
        shoulderMat.diffuseColor = new Color3(0.4, 0.3, 0.2);
        const ls2 = MeshBuilder.CreateSphere('blsh', { diameter: 0.18, segments: 6 }, scene);
        ls2.position = new Vector3(-0.26, 1.3, 0);
        ls2.parent = this.mesh;
        ls2.material = shoulderMat;
        const rs2 = ls2.clone('brsh');
        rs2.position.x = 0.26;
        rs2.parent = this.mesh;
        // 手臂
        const la = MeshBuilder.CreateCylinder('blarm', { height: 0.45, diameter: 0.1, tessellation: 6 }, scene);
        la.position = new Vector3(-0.26, 1.05, 0);
        la.parent = this.mesh;
        la.material = bodyMat;
        const ra = la.clone('brarm');
        ra.position.x = 0.26;
        ra.parent = this.mesh;
        // 脖子 + 头
        const skinMat = new StandardMaterial('bSkinMat', scene);
        skinMat.diffuseColor = new Color3(0.75, 0.6, 0.45);
        const neck = MeshBuilder.CreateCylinder('bNeck', { height: 0.1, diameter: 0.14, tessellation: 6 }, scene);
        neck.position.y = 1.4;
        neck.parent = this.mesh;
        neck.material = skinMat;
        const head = MeshBuilder.CreateSphere('bHead', { diameter: 0.35, segments: 8 }, scene);
        head.position.y = 1.65;
        head.parent = this.mesh;
        head.material = skinMat;
        // 头巾
        const bandanaMat = new StandardMaterial('bBandana', scene);
        bandanaMat.diffuseColor = new Color3(0.6, 0.1, 0.08);
        const bandana = MeshBuilder.CreateSphere('bBandana', { diameter: 0.37, segments: 8 }, scene);
        bandana.position.y = 1.7;
        bandana.scaling = new Vector3(1.05, 0.6, 1.05);
        bandana.parent = this.mesh;
        bandana.material = bandanaMat;
        // 武器（大刀）
        const weaponMat = new StandardMaterial('bWeaponMat', scene);
        weaponMat.diffuseColor = new Color3(0.7, 0.7, 0.75);
        weaponMat.specularColor = new Color3(0.9, 0.9, 0.9);
        const blade = MeshBuilder.CreateBox('bBlade', { width: 0.06, height: 0.8, depth: 0.18 }, scene);
        blade.position = new Vector3(0.32, 1.1, 0.1);
        blade.rotation.z = -0.3;
        blade.parent = this.mesh;
        blade.material = weaponMat;
    }
    createNameplate(scene, name, isBoss) {
        const plane = MeshBuilder.CreatePlane('mNameplate', { width: isBoss ? 3 : 2, height: 0.3 }, scene);
        const texture = new DynamicTexture('mNameTex', { width: 256, height: 40 }, scene, false);
        texture.hasAlpha = true;
        const ctx = texture.getContext();
        ctx.clearRect(0, 0, 256, 40);
        ctx.font = isBoss ? 'bold 22px Microsoft YaHei' : '20px Microsoft YaHei';
        ctx.textAlign = 'center';
        ctx.fillStyle = isBoss ? '#ff8800' : '#ff4444';
        ctx.fillText(name, 128, 30);
        texture.update();
        const mat = new StandardMaterial('mNameplateMat', scene);
        mat.diffuseTexture = texture;
        mat.emissiveColor = new Color3(1, 1, 1);
        mat.disableLighting = true;
        mat.backFaceCulling = false;
        plane.material = mat;
        return plane;
    }
    async tryLoadGLB(scene) {
        const model = await loadModel(scene, `/models/${this.type}.glb`);
        if (model) {
            this.mesh.getChildMeshes().forEach(m => {
                if (m !== this.nameplate && m !== this.hpFill && !m.name.startsWith('mhp')) {
                    m.dispose();
                }
            });
            model.root.parent = this.mesh;
            model.root.scaling.setAll(this.type === 'boss_tiger' ? 0.015 : 0.01);
            this.glbModel = model;
        }
    }
    updateFromServer(data) {
        this.targetPos.set(data.x, 0, data.z);
        this.hp = data.hp;
        this.maxHp = data.maxHp;
        const ratio = Math.max(0, this.hp / this.maxHp);
        this.hpFill.scaling.x = ratio;
        this.hpFill.position.x = -(1 - ratio) * 0.49;
        // 血条颜色
        const fillMat = this.hpFill.material;
        if (ratio > 0.5) {
            fillMat.diffuseColor = new Color3(0.9, 0.2, 0.1);
        }
        else if (ratio > 0.25) {
            fillMat.diffuseColor = new Color3(0.9, 0.5, 0.1);
        }
        else {
            fillMat.diffuseColor = new Color3(0.95, 0.05, 0.05);
            fillMat.emissiveColor = new Color3(0.5, 0.02, 0.02);
        }
    }
    interpolate() {
        this.lastPos.copyFrom(this.mesh.position);
        this.mesh.position = Vector3.Lerp(this.mesh.position, this.targetPos, 0.1);
        // 面向移动方向
        const dx = this.targetPos.x - this.mesh.position.x;
        const dz = this.targetPos.z - this.mesh.position.z;
        if (Math.abs(dx) > 0.01 || Math.abs(dz) > 0.01) {
            const targetRot = Math.atan2(dx, dz);
            this.mesh.rotation.y += (targetRot - this.mesh.rotation.y) * 0.1;
        }
        // 行走动画
        if (this.glbModel) {
            const moved = Vector3.Distance(this.mesh.position, this.lastPos) > 0.005;
            const anim = moved ? 'run' : 'idle';
            if (this.currentAnim !== anim) {
                playAnimation(this.glbModel.animations, anim);
                this.currentAnim = anim;
            }
        }
    }
    dispose() {
        this.mesh.dispose(false, true);
    }
}
